/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UPLOAD_ERROR_VISIBILITY
 * Date:         2026-09-15
 * Modified By:  Claude
 *
 * Description:
 * Component existed but was mounted nowhere - the user got only a toast and was navigated
 * away, so a fully-rejected upload looked like silence (verified live 2026-09-15: 99,143
 * rows rejected, zero feedback on screen). Now mounted by InventoryPage for every upload.
 * [2026-09-16] Renders the OUTCOME only - progress, then a verdict headline with loaded and
 * rejected counts. The fix instructions and the full list of affected rows live in the
 * completion email and its attached CSV, so there is one place to maintain and the item list
 * is not buried under a wall of guidance.
 *
 * File Size Check:
 * - Current: 139 lines -> ~215 lines
 * - Decision: Proceed (component limit ~150 is soft; split not warranted for one panel)
 *
 * Related Files:
 * - uploader/services/uploadErrorSummary.js (builds the groups)
 * - server/controllers/inventoryChunksController.js (serves sampleErrors)
 * - client/src/features/inventory/InventoryPage.tsx (mounts this)
 */
import React from "react";
import { getJobStats } from "../../services/inventoryUploads";

interface UploadStatusPanelProps {
  jobId: string;
  /** [2026-09-16] browser -> server chunk send progress, before processing begins */
  sendProgress?: { sent: number; total: number } | null;
  onJobNotFound?: () => void;
  onJobComplete?: () => void;
  onDismiss?: () => void;
  // [2026-09-17] ends a stalled job on the server (POST .../abort)
  onStop?: () => void;
}

interface ErrorGroup {
  code?: string;
  message?: string;
  field?: string;
  count?: number;
  title?: string;
  fixHint?: string;
  rowIndex?: number;
  examples?: { row?: number; item?: string }[];
}

export default function UploadStatusPanel({
  jobId,
  sendProgress,
  onJobNotFound,
  onJobComplete,
  onDismiss,
  onStop,
}: UploadStatusPanelProps) {
  const [data, setData] = React.useState<any>(null);
  const [error, setError] = React.useState<string | null>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = React.useRef(true);
  const hasNotifiedCompleteRef = React.useRef(false);

  // [2026-09-15] UPLOAD_ERROR_VISIBILITY: callbacks live in refs so the polling effect
  // depends ONLY on jobId. With them in the deps array, the parent's refetch-triggered
  // re-render handed the panel new callback identities, the effect restarted, reset the
  // notified flag, notified again -> infinite refetch loop (skeleton flicker on screen).
  const onJobCompleteRef = React.useRef(onJobComplete);
  const onJobNotFoundRef = React.useRef(onJobNotFound);
  React.useEffect(() => {
    onJobCompleteRef.current = onJobComplete;
    onJobNotFoundRef.current = onJobNotFound;
  });

  React.useEffect(() => {
    isMountedRef.current = true;
    hasNotifiedCompleteRef.current = false;
    setError(null);

    const poll = async () => {
      if (!isMountedRef.current) return;
      try {
        const s = await getJobStats(jobId);
        if (!isMountedRef.current) return;
        setData(s);
        setError(null);

        const isTerminal = ["DONE", "FAILED", "PARTIAL"].includes(s.status);
        if (isTerminal && !hasNotifiedCompleteRef.current) {
          hasNotifiedCompleteRef.current = true;
          onJobCompleteRef.current?.();
        }
        if (!isTerminal && isMountedRef.current) {
          timeoutRef.current = setTimeout(poll, 2000);
        }
      } catch (err: any) {
        if (!isMountedRef.current) return;
        const statusCode = err?.response?.status;
        const isClientError = statusCode >= 400 && statusCode < 500;
        const isServerError = statusCode >= 500;
        if (isClientError) {
          setError(statusCode === 404 ? "Job not found" : "Access denied");
          if (statusCode === 404) onJobNotFoundRef.current?.();
          return;
        }
        if (isServerError && isMountedRef.current) {
          timeoutRef.current = setTimeout(poll, 4000);
        } else {
          setError("Failed to load job status");
        }
      }
    };
    poll();

    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [jobId]);

  if (error) {
    return (
      <div className="p-3 border rounded border-warning my-2">
        <div className="text-sm text-warning">{error}</div>
      </div>
    );
  }

  if (!data) return <div className="text-sm my-2">Loading upload status…</div>;

  const processed = data.totals?.rowsProcessed ?? 0;
  const total = data.totals?.rowsDeclared || Math.max(1, processed || 1);
  const pct = total ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const success = data.totals?.success ?? 0;
  const errors = data.totals?.errors ?? 0;
  // [2026-09-17] CANCELLED added: it is terminal, but was omitted, so a cancelled job kept
  // polling and kept showing "Queued - waiting to start" with no Dismiss button.
  const isTerminal = ["DONE", "FAILED", "PARTIAL", "CANCELLED"].includes(data.status);
  // [2026-09-16] A job sits PENDING until the poller claims it - 8 seconds normally, up to 5
  // minutes if completeJob never fired. Showing "Processing... 0%" with a dead bar during that
  // window reads as stuck, because nothing IS processing yet.
  const queued = !isTerminal && !data.startedAt;
  // [2026-09-16] Between the poller claiming the job and the first row landing, it deletes the
  // seller's previous catalog - a single deleteMany that took 23 seconds for 97,296 items.
  // processedRows stays 0 throughout, so a "Processing" heading over a motionless bar read as
  // stuck. Name this phase instead.
  const clearing = !isTerminal && !!data.startedAt && !data.inventoryCleared && processed === 0;
  // [2026-09-16] while the browser is still pushing chunks the server has processed nothing,
  // so a server-progress bar sits at 0% and looks broken. Show the send phase instead.
  const sending = !!sendProgress && sendProgress.total > 0 && sendProgress.sent < sendProgress.total;
  const sendPct = sending
    ? Math.min(100, Math.round((sendProgress!.sent * 100) / sendProgress!.total))
    : 0;
  const nothingLoaded = isTerminal && success === 0 && errors > 0;
  // [2026-09-17] UPLOAD_FAILED_VISIBILITY. A job that never processed a row has success=0 AND
  // errors=0, so it fell through every branch below to "Upload complete - 0 items loaded", in
  // green. A failed upload was reported to the customer as a success. Branch it first.
  const failed = ["FAILED", "CANCELLED"].includes(data.status);
  // A job with no startedAt after this long is not waiting its turn, it is not going to start.
  const stuckMs = 3 * 60 * 1000;
  const ageMs = data.createdAt ? Date.now() - new Date(data.createdAt).getTime() : 0;
  const stalled = queued && !sending && ageMs > stuckMs;

  return (
    <div className="p-3 border rounded my-2">
      {/* [2026-09-16] turning hourglass, so the clearing phase visibly moves */}
      <style>{`
        @keyframes uploadHourglassTurn {
          0%, 45% { transform: rotate(0deg); }
          50%, 95% { transform: rotate(180deg); }
          100% { transform: rotate(360deg); }
        }
        .upload-hourglass {
          display: inline-block;
          animation: uploadHourglassTurn 2s ease-in-out infinite;
        }
      `}</style>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="text-sm">
          {sending && <b>Sending your file to the server…</b>}
          {/* [2026-09-17] !stalled: stalled is a SUBSET of queued, so both headlines rendered
              together - "Queued - waiting to start" directly above "This upload has not started". */}
          {!sending && queued && !stalled && <b>Queued — waiting to start…</b>}
          {!sending && !queued && clearing && (
            <b>
              <span className="upload-hourglass" role="img" aria-label="working">
                ⏳
              </span>{" "}
              Clearing your previous inventory…
            </b>
          )}
          {!sending && !queued && !clearing && !isTerminal && <b>Processing your file…</b>}
          {isTerminal && !failed && nothingLoaded && (
            <b style={{ color: "var(--color-status-red)" }}>
              Upload finished — no items were loaded
            </b>
          )}
          {failed && (
            <b style={{ color: "var(--color-status-red)" }}>
              Upload did not complete — your inventory was not changed
            </b>
          )}
          {stalled && (
            <b style={{ color: "var(--color-status-orange)" }}>
              This upload has not started
            </b>
          )}
          {isTerminal && !failed && !nothingLoaded && errors > 0 && (
            <b style={{ color: "var(--color-status-orange)" }}>
              Upload finished — {success.toLocaleString()} items loaded, {errors.toLocaleString()} rows need fixing
            </b>
          )}
          {isTerminal && !failed && errors === 0 && (
            <b style={{ color: "var(--color-status-green)" }}>
              Upload complete — {success.toLocaleString()} items loaded
            </b>
          )}
        </span>
        <span className="text-sm">
          {sending
            ? `${sendProgress!.sent} / ${sendProgress!.total} parts sent (${sendPct}%)`
            : queued
              ? `${total.toLocaleString()} rows ready`
              : clearing
                ? "Making room for the new file"
              : `${processed.toLocaleString()}/${total.toLocaleString()} rows (${pct}%)`}
          {isTerminal && onDismiss && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary ms-3"
              onClick={onDismiss}
            >
              Dismiss
            </button>
          )}
          {/* [2026-09-17] a job that will never start had no exit: Dismiss is gated on a
              terminal status, so the only stuck state the user can reach was the one with no
              button. This ends it server-side, not just in local state. */}
          {stalled && onStop && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger ms-3"
              onClick={onStop}
            >
              Stop this upload
            </button>
          )}
        </span>
      </div>
      <div className="w-full bg-gray-200 h-2 rounded" style={{ background: "#e5e7eb", height: 8, borderRadius: 4 }}>
        <div
          style={{
            width: queued || clearing ? "100%" : `${sending ? sendPct : pct}%`,
            height: 8,
            borderRadius: 4,
            background: failed
              ? "var(--color-status-red)"
              : queued || clearing
              ? "#d1d5db"
              : nothingLoaded
              ? "var(--color-status-red)"
              : errors > 0 && isTerminal
                ? "var(--color-status-orange)"
                : "var(--color-status-green)",
          }}
        />
      </div>
      <div className="mt-2 text-xs">
        {failed
          ? data.failureReason ||
            "The file did not arrive in full, so nothing was changed. Please upload it again."
          : stalled
          ? "This upload has not started and is not going to. Nothing has been changed. Stop it and try again."
          : clearing
          ? "Removing the items from your last upload before the new ones are added. This can take a moment on a large catalogue."
          : queued
          ? "Your file has arrived and is waiting its turn. This usually starts within a few seconds."
          : sending
          ? "Keep this page open until the upload finishes."
          : `Loaded: ${success.toLocaleString()}  |  Rejected: ${errors.toLocaleString()}`}
      </div>

      {/* [2026-09-16] The screen states the OUTCOME; the email and its attached CSV carry the
          fix instructions and every affected row. Repeating the guidance here meant two places
          to maintain and a long wall of text above the item list. */}
      {isTerminal && !failed && errors > 0 && (
        <div className="mt-2 text-xs">
          {/* [2026-09-17] The report is a LINK now, not an attachment - it moved to R2 so a
              1 MB workbook is not written into the mail record. Saying "attached" sent the
              customer hunting for a paperclip that is not there. */}
          We have emailed you a link to download every row that needs fixing.
        </div>
      )}
    </div>
  );
}
